import {
  createSlice,
  createEntityAdapter,
  createSelector,
} from "@reduxjs/toolkit";
import { selectTaskById } from "./tasksSlice";
import { normalize } from "normalizr";
import { commentsSchema, commentEntity } from "../schemas";
import {
  readEntity,
  createEntity,
  updateEntity,
  deleteEntity,
} from "../../data/delicDataClient";
import {
  upsertCommentsFromCommentables,
  upsertComments,
  createCommentSuccess,
} from "../actions";
import { selectComponentById } from "./componentsSlice";
import get from "lodash/get";

const commentsAdapter = createEntityAdapter();

export const getFolderChildFileCommentTargetKey = ({ componentId, fileId }) =>
  componentId && fileId ? `${componentId}:${fileId}` : "";

export const slice = createSlice({
  name: "comments",
  initialState: commentsAdapter.getInitialState({
    folderChildThreads: {},
  }),
  reducers: {
    fetchCommentsStart(state) {
      state.isLoading = true;
    },
    fetchCommentsSuccess(state, { payload }) {
      state.isLoading = false;
      const { comments } = payload;

      commentsAdapter.upsertMany(state, comments);
    },
    updateCommentSuccess(state, { payload }) {
      const { comment } = payload;
      const normalized = normalize(comment, commentEntity);
      commentsAdapter.upsertOne(
        state,
        normalized.entities.comments[comment.id]
      );
    },
    deleteCommentSuccess(state, { payload }) {
      const { commentId, folderChildTargetKey } = payload;
      commentsAdapter.removeOne(state, commentId);
      if (folderChildTargetKey && state.folderChildThreads[folderChildTargetKey]) {
        const thread = state.folderChildThreads[folderChildTargetKey];
        thread.comments = thread.comments.filter((id) => id !== commentId);
        thread.numberOfComments = Math.max(0, thread.numberOfComments - 1);
      }
    },
    fetchFolderChildFileCommentsSuccess(state, { payload }) {
      state.isLoading = false;
      const {
        targetKey,
        comments = {},
        ids = [],
        file,
        numberOfComments = 0,
      } = payload;

      if (comments) {
        commentsAdapter.upsertMany(state, comments);
      }
      state.folderChildThreads[targetKey] = {
        file,
        comments: ids,
        numberOfComments,
      };
    },
    createFolderChildFileCommentSuccess(state, { payload }) {
      const { comment, targetKey } = payload;
      const normalized = normalize(comment, commentEntity);
      const normalizedComment = normalized.entities.comments[comment.id];
      commentsAdapter.upsertOne(state, normalizedComment);

      if (!state.folderChildThreads[targetKey]) {
        state.folderChildThreads[targetKey] = {
          comments: [],
          numberOfComments: 0,
        };
      }
      const thread = state.folderChildThreads[targetKey];
      if (!thread.comments.includes(comment.id)) {
        thread.comments = [comment.id].concat(thread.comments);
        thread.numberOfComments += 1;
      }
    },
    onFail(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(upsertCommentsFromCommentables, (state, { payload }) => {
      const { comments } = payload;
      const normalized = normalize({ comments }, commentsSchema);
      if (normalized.entities.comments) {
        commentsAdapter.upsertMany(state, normalized.entities.comments);
      }
    });
    builder.addCase(upsertComments, (state, { payload }) => {
      const { comments } = payload;
      if (comments) {
        commentsAdapter.upsertMany(state, comments);
      }
    });
    builder.addCase(createCommentSuccess, (state, { payload }) => {
      const { comment } = payload;
      const normalized = normalize(comment, commentEntity);
      commentsAdapter.addOne(state, normalized.entities.comments[comment.id]);
    });
  },
});

const reducer = slice.reducer;
export default reducer;

export const {
  onFail,
  updateCommentSuccess,
  deleteCommentSuccess,
  fetchCommentsSuccess,
  fetchFolderChildFileCommentsSuccess,
  createFolderChildFileCommentSuccess,
} = slice.actions;

export const {
  selectById: selectCommentById,
  selectIds: selectCommentIds,
  selectEntities: selectCommentEntities,
  selectAll: selectAllComments,
  selectTotal: selectTotalComments,
} = commentsAdapter.getSelectors((state) => state.comments);

export const selectCommentsByCommentableId = (commentableId) =>
  createSelector(
    [
      (state) => {
        const task = selectTaskById(state, commentableId);
        if (task) {
          return task;
        }
        const component = selectComponentById(state, commentableId);
        if (component) {
          return component;
        }
      },
      (state) => state.comments.entities,
    ],
    (commentable, comments) => {
      if (commentable) {
        const commentableComments = get(commentable, "comments", []);
        return commentableComments.reduce(
          (arr, id) => (comments[id] ? arr.concat(comments[id]) : arr),
          []
        );
      }
      return [];
    }
  );

export const selectFolderChildFileComments = (componentId, fileId) =>
  createSelector(
    [
      (state) =>
        state.comments.folderChildThreads[
          getFolderChildFileCommentTargetKey({ componentId, fileId })
        ],
      (state) => state.comments.entities,
    ],
    (thread, comments) => {
      if (!thread) return [];
      return thread.comments.reduce(
        (arr, id) => (comments[id] ? arr.concat(comments[id]) : arr),
        []
      );
    }
  );

export const selectFolderChildFileCommentCount = (componentId, fileId) =>
  createSelector(
    [
      (state) =>
        state.comments.folderChildThreads[
          getFolderChildFileCommentTargetKey({ componentId, fileId })
        ],
    ],
    (thread) => (thread ? thread.numberOfComments : 0)
  );

// ------------------- CREATE ------------------ //

export const createComment = ({ comment, commentableId }) => async (
  dispatch
) => {
  try {
    const newComment = await createEntity("Comment", comment);
    if (!newComment) {
      dispatch(onFail("Error when creating comment"));
    } else {
      dispatch(createCommentSuccess({ comment: newComment, commentableId }));
    }
  } catch (err) {
    dispatch(onFail(err.toString()));
  }
};

export const createFolderChildFileComment = ({
  personId,
  componentId,
  fileId,
  text,
}) => async (dispatch) => {
  const targetKey = getFolderChildFileCommentTargetKey({ componentId, fileId });
  if (!personId || !componentId || !fileId || !text) {
    return;
  }

  try {
    const comment = await createEntity("FolderChildFileComment", {
      personId,
      componentId,
      fileId,
      text,
    });
    if (!comment) {
      dispatch(onFail("Error when creating folder child file comment"));
    } else {
      dispatch(createFolderChildFileCommentSuccess({ comment, targetKey }));
    }
  } catch (err) {
    dispatch(onFail(err.toString()));
  }
};

// ------------------- UPDATE ------------------ //

export const updateComment = ({ personId, text, commentId }) => async (
  dispatch
) => {
  try {
    const payload = {
      personId,
      input: {
        text,
        id: commentId,
      },
    };
    const comment = await updateEntity("Comment", payload);

    if (!comment) {
      dispatch(onFail("Didn't update comment"));
    }

    dispatch(updateCommentSuccess({ comment }));
  } catch (err) {
    dispatch(onFail(err.toString()));
  }
};

export const deleteComment = ({
  personId,
  commentId,
  commentableId,
  folderChildTargetKey,
}) => async (dispatch) => {
  try {
    const payload = {
      personId: personId,
      commentId,
    };
    const deletedComment = await deleteEntity("Comment", payload);

    if (!deletedComment) {
      dispatch(onFail("Didn't delete comment"));
    }

    dispatch(
      deleteCommentSuccess({ commentId, commentableId, folderChildTargetKey })
    );
  } catch (err) {
    dispatch(onFail(err.toString()));
  }
};

export const fetchFolderChildFileComments = ({
  personId,
  componentId,
  fileId,
}) => async (dispatch) => {
  const targetKey = getFolderChildFileCommentTargetKey({ componentId, fileId });
  if (!personId || !componentId || !fileId) {
    return;
  }

  try {
    const payload = {
      personId,
      componentId,
      fileId,
    };
    const fileComments = await readEntity("FileComments", payload);

    if (!fileComments) {
      dispatch(onFail("Fetch of folder child file comments failed"));
      return;
    }

    const comments = get(fileComments, "comments", []);
    const { result, entities } = normalize({ comments }, commentsSchema);

    dispatch(
      fetchFolderChildFileCommentsSuccess({
        targetKey,
        comments: entities.comments || {},
        ids: result.comments || [],
        file: fileComments.file,
        numberOfComments: fileComments.numberOfComments || 0,
      })
    );
  } catch (err) {
    dispatch(onFail(err.toString()));
  }
};

export const fetchComments = ({ commentableId }) => async (dispatch) => {
  if (!commentableId) {
    return;
  }

  try {
    const payload = {
      componentId: commentableId,
    };
    const componentWithComments = await readEntity(
      "ComponentComments",
      payload
    );

    if (!componentWithComments) {
      dispatch(onFail("Fetch of components failed"));
    }
    const { result, entities } = normalize(
      componentWithComments,
      commentsSchema
    );

    dispatch(
      fetchCommentsSuccess({
        comments: entities.comments,
        ids: result.comments,
        commentableId,
      })
    );
  } catch (err) {
    dispatch(onFail(err.toString()));
  }
};
